import { useCreateApplicationsForSuggestedBreeders } from '@application/api';
import { SUGGESTED_BREEDERS_FOR_APPLICATIONS_LIMIT } from '@application/api/suggestions/useGetSuggestedBreeders';
import { useShowToast } from '@application/utils';
import { ApplicationsSuggestionModal, ApplicationsSuggestionTable } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { Form, FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useGetLatestApplication } from '../pages/ApplicationPageController/api/useGetLatestApplication';
import type { MtApplicationsSuggestionModalControllerProps } from './types';

export const MtApplicationsSuggestionModalController: FC<
  MtApplicationsSuggestionModalControllerProps
> = (props) => {
  const { modal, suggestionRows } = props;

  const { showToast } = useShowToast();
  const { translate } = useTranslation();

  const { createApplicationsForSuggestedBreeders, createApplicationsForSuggestedBreedersLoading } =
    useCreateApplicationsForSuggestedBreeders();
  const { application: latestApplication } = useGetLatestApplication();

  const form = useFormik({
    initialValues: {
      selectedBreeders: Array(SUGGESTED_BREEDERS_FOR_APPLICATIONS_LIMIT).fill(false),
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      if (!suggestionRows || !latestApplication) {
        return;
      }

      const selectedBreeders = suggestionRows?.filter(
        (_, index) => !!(values.selectedBreeders ?? [])[index],
      );
      const breederValues = selectedBreeders.reduce(
        // eslint-disable-next-line no-return-assign
        (acc, curr) => (
          (acc[`${curr.id}-${curr.breedId}`] = {
            accommodation: latestApplication.accommodation,
            experiences: latestApplication.experiences,
            genderPreferences: latestApplication.genderPreferences,
            otherAnimals: latestApplication.otherAnimals,
            timeAlone: latestApplication.timeAlone,
            trainers: latestApplication.trainers,
            breedPreferenceId: curr.breedId,
            // eslint-disable-next-line no-sequences
          }),
          acc
        ),
        {},
      );

      await createApplicationsForSuggestedBreeders(breederValues);

      showToast(translate('common.toasts.applicationsSent'), {
        type: 'success',
      });

      resetForm();

      if (modal.onCloseClick) {
        modal.onCloseClick();
      }
    },
  });

  if (!suggestionRows?.length) {
    return null;
  }

  return (
    <FormikProvider value={form}>
      <ApplicationsSuggestionModal modal={modal}>
        <Form>
          <ApplicationsSuggestionTable
            rows={suggestionRows}
            canSubmit={form.values.selectedBreeders.includes(true)}
            isSubmitting={createApplicationsForSuggestedBreedersLoading}
          />
        </Form>
      </ApplicationsSuggestionModal>
    </FormikProvider>
  );
};
